/**
 * dataStore.js — Singleton data store for all real-time data.
 *
 * Lives outside React. The WebSocket handler writes here; page components
 * subscribe to only the keys they need via useStoreData().
 *
 * Batching: multiple rapid writes are coalesced into a single notification
 * per animation frame, so React never re-renders more than ~60 times/sec
 * (in practice far less, since updates cluster together).
 *
 * Keys:
 *   odds           → Map<id, record>   (converted to array by the hook)
 *   arbitrage      → Array of arb records
 *   middles        → Array of middle records
 *   projections    → Array of projection records (all leagues)
 *   futures        → Array of futures market objects
 *   futuresLeagues → Array of league IDs with futures data
 *   sportsbooks    → Array of sportsbook config objects
 *   markets        → Array of market config objects
 *   leagues        → Array of league config objects
 *   bettingMetrics → Array of metric records
 *   lastUpdate     → Date or null
 */

class DataStore {
  constructor() {
    this._data = {
      odds: new Map(),
      arbitrage: [],
      middles: [],
      projections: [],
      futures: [],           // NEW
      futuresLeagues: [],    // NEW
      sportsbooks: [],
      markets: [],
      leagues: [],
      teams: [],
      bettingMetrics: [],
      lastUpdate: null,
    };

    this._listeners = new Map();
    this._dirtyKeys = new Set();
    this._flushTimer = null;
  }

  subscribe(key, callback) {
    if (!this._listeners.has(key)) {
      this._listeners.set(key, new Set());
    }
    this._listeners.get(key).add(callback);
    return () => this._listeners.get(key).delete(callback);
  }

  get(key) {
    return this._data[key];
  }

  // ─── Odds (Map-based) ─────────────────────────────────

  setOddsSnapshot(items, sportsbooks, markets, leagues, teams) {
    this._data.odds.clear();
    for (const item of items) {
      const id = item.id ?? Date.now();
      if (!item.id) item.id = id;
      this._data.odds.set(id, item);
    }
    this._scheduleFlush('odds');

    if (sportsbooks) {
      this._data.sportsbooks = sportsbooks;
      this._scheduleFlush('sportsbooks');
    }

    if (markets) {
      this._data.markets = markets;
      this._scheduleFlush('markets');
    }

    if (leagues) {
      this._data.leagues = leagues;
      this._scheduleFlush('leagues');
    }

    if (teams) {
      this._data.teams = teams;
      this._scheduleFlush('teams');
    }

    this._data.lastUpdate = new Date();
    this._scheduleFlush('lastUpdate');
  }

  updateOdds(deltas) {
    for (const item of deltas) {
      if (item.deleted) {
        this._data.odds.delete(item.id);
      } else {
        const id = item.id ?? Date.now();
        if (!item.id) item.id = id;
        this._data.odds.set(id, item);
      }
    }
    this._scheduleFlush('odds');
    this._data.lastUpdate = new Date();
    this._scheduleFlush('lastUpdate');
  }

  // ─── Arbitrage ─────────────────────────────────────────

  setArbitrage(arbs) {
    this._data.arbitrage = arbs;
    this._scheduleFlush('arbitrage');
    this._data.lastUpdate = new Date();
    this._scheduleFlush('lastUpdate');
  }

  updateArbitrage(deltas) {
    const map = new Map(this._data.arbitrage.map(a => [a.arb_key, a]));
    for (const arb of deltas) {
      map.set(arb.arb_key, arb);
    }
    this._data.arbitrage = Array.from(map.values());
    this._scheduleFlush('arbitrage');
    this._data.lastUpdate = new Date();
    this._scheduleFlush('lastUpdate');
  }

  removeArbitrage(keys) {
    this._data.arbitrage = this._data.arbitrage.filter(a => !keys.includes(a.arb_key));
    this._scheduleFlush('arbitrage');
    this._data.lastUpdate = new Date();
    this._scheduleFlush('lastUpdate');
  }

  // ─── Middles ───────────────────────────────────────────

  setMiddles(middles) {
    this._data.middles = middles;
    this._scheduleFlush('middles');
    this._data.lastUpdate = new Date();
    this._scheduleFlush('lastUpdate');
  }

  updateMiddles(deltas) {
    const map = new Map(this._data.middles.map(m => [m.middle_key, m]));
    for (const m of deltas) {
      map.set(m.middle_key, m);
    }
    this._data.middles = Array.from(map.values());
    this._scheduleFlush('middles');
    this._data.lastUpdate = new Date();
    this._scheduleFlush('lastUpdate');
  }

  removeMiddles(keys) {
    this._data.middles = this._data.middles.filter(m => !keys.includes(m.middle_key));
    this._scheduleFlush('middles');
    this._data.lastUpdate = new Date();
    this._scheduleFlush('lastUpdate');
  }

  // ─── Projections ───────────────────────────────────────

  setProjections(projections) {
    this._data.projections = projections;
    this._scheduleFlush('projections');
  }

  updateProjections(deltas) {
    const map = new Map(this._data.projections.map(p => [p._key, p]));
    for (const p of deltas) {
      map.set(p._key, p);
    }
    this._data.projections = Array.from(map.values());
    this._scheduleFlush('projections');
  }

  // ─── Futures ───────────────────────────────────────────  NEW

  setFutures(markets, leagues) {
    this._data.futures = markets || [];
    this._data.futuresLeagues = leagues || [];
    this._scheduleFlush('futures');
    this._scheduleFlush('futuresLeagues');
    this._data.lastUpdate = new Date();
    this._scheduleFlush('lastUpdate');
  }

  updateFutures(deltas) {
    const map = new Map(this._data.futures.map(m => [m.future_id, m]));
    for (const m of deltas) {
      map.set(m.future_id, m);
    }
    this._data.futures = Array.from(map.values());
    this._scheduleFlush('futures');
    this._data.lastUpdate = new Date();
    this._scheduleFlush('lastUpdate');
  }

  // ─── Simple setters ────────────────────────────────────

  setSportsbooks(sportsbooks) {
    this._data.sportsbooks = sportsbooks;
    this._scheduleFlush('sportsbooks');
  }

  setMarkets(markets) {
    this._data.markets = markets;
    this._scheduleFlush('markets');
  }

  setLeagues(leagues) {
    this._data.leagues = leagues;
    this._scheduleFlush('leagues');
  }

  setTeams(teams) {
    this._data.teams = teams;
    this._scheduleFlush('teams');
  }

  setBettingMetrics(metrics) {
    this._data.bettingMetrics = metrics;
    this._scheduleFlush('bettingMetrics');
  }

  // ─── Flush batching ────────────────────────────────────

  _scheduleFlush(key) {
    this._dirtyKeys.add(key);
    if (!this._flushTimer) {
      this._flushTimer = setTimeout(() => {
        const dirty = new Set(this._dirtyKeys);
        this._dirtyKeys.clear();
        this._flushTimer = null;
        for (const k of dirty) {
          const cbs = this._listeners.get(k);
          if (cbs) cbs.forEach(cb => cb());
        }
      }, 1000);
    }
  }
}

const dataStore = new DataStore();
export default dataStore;